import { Alert, Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { Download } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePatientsGetById } from '~/backend/connector-next-api/connector-next-components';
import type { SeriesDto } from '~/backend/connector-next-api/connector-next-schemas';
import { postCourseStage } from '~/backend/connector-next-api/courseStageClient';
import {
    postCasePrimaryDataset,
    postPrimaryDataset,
    type PrimaryDatasetKind,
} from '~/backend/connector-next-api/primaryDatasetClient';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import NameDialog from '~/components/Dialogs/NameDialog';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import {
    PERMISSION,
    useConnectorIdentity,
} from '~/hooks/auth/useConnectorIdentity';
import { useIsOpeningInViewer, useOpenInViewer } from '~/hooks/openInViewer';
import {
    useDatasetInfo,
    usePatientCases,
    usePatientRetrievals,
    usePatientWrite,
} from '../hooks';
import {
    caseKeepsAnImage,
    casesOf,
    defaultCaseName,
    filterDayGroupsByModality,
    primaryLeftOutOfFirstCase,
    seriesDisplayName,
    setsLeavingWithImages,
    flattenSeries,
    groupSeriesTreeByDay,
    IMPORT_PROGRESS_POLL_MS,
    primariesInView,
    scopeDetail,
    scopeToOpen,
    isCaseMemberKind,
    seriesComposition,
    shownSeriesOf,
    type CaseScope,
    type CourseStage,
} from '../model';
import CaseChips, { type CaseAction } from './CaseChips';
import CaseSelectionBar from './CaseSelectionBar';
import DatasetDayGroup from './DatasetDayGroup';
import type { DatasetPrimary } from './DatasetRow';
import DatasetToolbar, { type TickState } from './DatasetToolbar';
import DrilldownHeader from './DrilldownHeader';
import PatientImportSection from './PatientImportSection';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';

/** Mirrors the Connector's cap on a case name. */
const CASE_NAME_MAX_LENGTH = 128;

/** What the name dialog is naming: a new case of the given datasets, or an existing case. */
type CaseNaming =
    | { mode: 'create'; datasetIds: readonly string[]; name: string }
    | { mode: 'rename'; caseId: string; name: string };

type PatientDrilldownProps = {
    patientId: string;
    onClose: () => void;
    width: number;
};

/**
 * Docked right-hand card for one patient: identity header with the panel
 * actions, the patient's cases, and the datasets (series) grouped by day,
 * narrowable by modality, each with its details on demand.
 * The header's Open loads the case in view, the datasets ticked, or with
 * neither the whole patient.
 *
 * The panel writes things about the course and nothing about the data: the
 * course stage (with the stages marked done), the primary planning image and
 * structure set, the patient's cases, and, through the header's tags field,
 * the clinical tags. Without PatientModify it shows them with no control.
 * Once the patient has a case, the primaries are chosen per case, in that
 * case's view; the panel then opens a case, or the datasets ticked, as one
 * scope.
 *
 * Mount with `key={patientId}`: the remount is what guarantees no panel state
 * carries across patients.
 */
export default function PatientDrilldown({
    patientId,
    onClose,
    width,
}: PatientDrilldownProps) {
    const intl = useIntl();
    const canModify = useConnectorIdentity().can(PERMISSION.patientModify);
    const retrievals = usePatientRetrievals(patientId);
    // Re-read while series are still arriving, so each one joins the dataset list as it lands
    // rather than when the panel is next opened. Retrievals that cannot be read may still be
    // arriving, so they keep this read going too.
    const { data, isLoading, isError } = usePatientsGetById(
        { pathParams: { id: patientId } },
        {
            refetchInterval:
                retrievals.summary.active || retrievals.isError
                    ? IMPORT_PROGRESS_POLL_MS
                    : false,
        }
    );

    // Focus lands here on open (once per patient via the key remount) and
    // returns to the originating row on close. The panel is still sliding
    // open inside a clipped box at that moment, so the focus must not scroll
    // it into view.
    const rootRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        rootRef.current?.focus({ preventScroll: true });
    }, []);
    const info = useDatasetInfo(rootRef);

    const activeCases = useMemo(
        () => casesOf(data, { archived: false }),
        [data]
    );
    const archivedCases = useMemo(
        () => casesOf(data, { archived: true }),
        [data]
    );
    const hasCases = (data?.cases?.length ?? 0) > 0;

    // The case in view; undefined is every object. A case deleted elsewhere
    // drops the view back to every object rather than showing an empty list.
    const [viewCaseChoice, setViewCaseChoice] = useState<string>();
    // A patient with cases opens on its work case. Decided once, when the
    // detail first arrives, so choosing every object afterwards sticks.
    const viewDefaulted = useRef(false);
    useEffect(() => {
        if (data === undefined || viewDefaulted.current) {
            return;
        }
        viewDefaulted.current = true;
        if (data.workCaseId) {
            setViewCaseChoice(data.workCaseId);
        }
    }, [data]);
    const viewCase = data?.cases?.find((c) => c.id === viewCaseChoice);
    const viewCaseId = viewCase?.id ?? undefined;
    // Datasets ticked for a case. Cleared when the view or the modality filter
    // changes, and read only where the list shows them (`ticked` below), so
    // nothing ticked is ever out of sight, even once the detail refreshes.
    const [tickChoice, setTicked] = useState<ReadonlySet<string>>(new Set());
    const selectView = useCallback((caseId: string | undefined) => {
        setViewCaseChoice(caseId);
        setTicked(new Set());
    }, []);
    const handleTick = useCallback((datasetId: string) => {
        setTicked((current) => {
            const next = new Set(current);
            if (!next.delete(datasetId)) {
                next.add(datasetId);
            }
            return next;
        });
    }, []);

    const allSeries = useMemo(() => flattenSeries(data), [data]);
    const viewDetail = useMemo(
        () =>
            data !== undefined && viewCaseId !== undefined
                ? scopeDetail(data, { caseId: viewCaseId })
                : data,
        [data, viewCaseId]
    );
    const viewSeries = useMemo(() => flattenSeries(viewDetail), [viewDetail]);
    // The designations in view. With cases and no case in view there are
    // none: the patient holds none once it has a case.
    const primaries = primariesInView(data, viewCaseId);
    const dayGroups = useMemo(
        () => groupSeriesTreeByDay(intl, viewSeries, primaries.structureSetId),
        [intl, viewSeries, primaries.structureSetId]
    );
    // Modalities the list is narrowed to; empty shows every modality.
    const [modalityFilter, setModalityFilter] = useState<ReadonlySet<string>>(
        new Set()
    );
    const toggleModality = useCallback((modality: string) => {
        setModalityFilter((current) => {
            const next = new Set(current);
            if (!next.delete(modality)) {
                next.add(modality);
            }
            return next;
        });
        setTicked(new Set());
    }, []);
    const composition = useMemo(
        () => seriesComposition(viewSeries),
        [viewSeries]
    );
    // Only modalities the view holds narrow it: one it lacks has no chip to
    // turn it off with, and would leave the list empty.
    const shownFilter = useMemo(
        () =>
            new Set(
                [...modalityFilter].filter((m) =>
                    composition.some((c) => c.modality === m)
                )
            ),
        [modalityFilter, composition]
    );
    const groups = useMemo(
        () => filterDayGroupsByModality(dayGroups, shownFilter),
        [dayGroups, shownFilter]
    );
    const shownSeries = useMemo(() => shownSeriesOf(groups), [groups]);
    // Stable while the datasets are, so a hover re-renders only the rows it touches.
    const infoOffer = useMemo(
        () => ({ controls: info.controls, siblings: allSeries }),
        [info.controls, allSeries]
    );
    // What can be ticked for a case among what the list shows.
    const tickable = useMemo(
        () =>
            shownSeries
                .filter(isCaseMemberKind)
                .map((series) => series.id as string),
        [shownSeries]
    );
    const ticked = useMemo(
        () => new Set(tickable.filter((id) => tickChoice.has(id))),
        [tickable, tickChoice]
    );
    const tickState: TickState | undefined =
        tickable.length === 0
            ? undefined
            : tickable.every((id) => ticked.has(id))
              ? 'all'
              : tickable.some((id) => ticked.has(id))
                ? 'some'
                : 'none';
    const handleTickAll = () =>
        setTicked((current) => {
            const next = new Set(current);
            if (tickState === 'all') {
                tickable.forEach((id) => next.delete(id));
            } else {
                tickable.forEach((id) => next.add(id));
            }
            return next;
        });

    // Guarded by the detail's version, so a stage set from another tab since
    // this panel loaded conflicts rather than being overwritten.
    const {
        write: setCourseStage,
        isSaving,
        isError: stageError,
    } = usePatientWrite(
        { id: patientId, version: data?.version },
        (
            change: { stage: CourseStage; done: readonly CourseStage[] },
            id,
            version
        ) => postCourseStage(id, change.stage, [...change.done], version)
    );

    // The primaries steer every later open: the image is registration's pivot
    // and the fallback an Open lands on. So they are the patient's own
    // book-keeping, not this session's preference.
    const {
        write: setPrimaryDataset,
        isSaving: primarySaving,
        isError: primaryError,
    } = usePatientWrite(
        { id: patientId, version: data?.version },
        (
            change: {
                kind: PrimaryDatasetKind;
                datasetId: string | null;
                /** The case the designation is made on; absent for the patient's own. */
                caseId?: string;
            },
            id,
            version
        ) =>
            change.caseId === undefined
                ? postPrimaryDataset(id, change.kind, change.datasetId, version)
                : postCasePrimaryDataset(
                      id,
                      change.caseId,
                      change.kind,
                      change.datasetId,
                      version
                  )
    );

    // Every write to the patient reports saving while any is in flight, so
    // Open waits for a case change and a designation alike.
    const {
        write: runCaseCommand,
        isSaving: casesSaving,
        isError: casesError,
    } = usePatientCases({ id: patientId, version: data?.version });

    // Switching the primary image is confirmed; the pending switch is held
    // here until the user answers. Designating a first image, and every
    // structure-set designation, applies straight away — there is nothing to
    // displace.
    const [pendingImageSwitch, setPendingImageSwitch] = useState<string>();

    const handleTogglePrimary = useCallback<DatasetPrimary['onToggle']>(
        ({ kind, datasetId, isPrimary }) => {
            if (isPrimary) {
                // Clearing is a statement in its own right: there is no
                // primary of this kind and the open falls back to its defaults.
                setPrimaryDataset({
                    kind,
                    datasetId: null,
                    caseId: viewCaseId,
                });
                return;
            }
            if (kind === 'image' && primaries.imageId !== datasetId) {
                // Only when something is displaced. Designating the first
                // image takes nothing away, and a set is never confirmed: it
                // brings its own image along, which is the point of it.
                if (primaries.imageId) {
                    setPendingImageSwitch(datasetId);
                    return;
                }
            }
            setPrimaryDataset({ kind, datasetId, caseId: viewCaseId });
        },
        [setPrimaryDataset, primaries.imageId, viewCaseId]
    );

    // Memoised because DatasetRow is memoised: a fresh object every render
    // would re-render every row of the panel on any state change at all.
    const primary: DatasetPrimary | undefined = useMemo(
        () =>
            // With cases, designations are made in a case's view only.
            !canModify ||
            data?.version === undefined ||
            (hasCases && viewCaseId === undefined)
                ? undefined
                : {
                      imageId: primaries.imageId,
                      structureSetId: primaries.structureSetId,
                      onToggle: handleTogglePrimary,
                      busy: primarySaving,
                  },
        [
            canModify,
            data?.version,
            hasCases,
            viewCaseId,
            primaries.imageId,
            primaries.structureSetId,
            handleTogglePrimary,
            primarySaving,
        ]
    );

    const [naming, setNaming] = useState<CaseNaming>();
    // A first case that leaves the patient's primary image out waits here for
    // the user to accept that the designation will not carry over.
    const [pendingFirstCase, setPendingFirstCase] = useState<{
        name: string;
        datasetIds: readonly string[];
        leftOut: NonNullable<ReturnType<typeof primaryLeftOutOfFirstCase>>;
    }>();
    const [pendingDelete, setPendingDelete] = useState<string>();
    // A removal that takes structure sets the user did not tick along with
    // their image, held until the user confirms it.
    const [pendingRemoval, setPendingRemoval] = useState<{
        caseId: string;
        datasetIds: readonly string[];
        alsoLeaving: readonly SeriesDto[];
    }>();
    // The ticks stay until the change is made, so a failed one can be sent again.
    const clearTicks = () => setTicked(new Set());
    const removeFromCase = (caseId: string, datasetIds: readonly string[]) =>
        runCaseCommand(
            { type: 'members', caseId, remove: datasetIds },
            undefined,
            clearTicks
        );

    const newCaseIds: readonly string[] = [...ticked];

    const createCase = (name: string, datasetIds: readonly string[]) =>
        runCaseCommand({
            type: 'create',
            name,
            datasetIds,
            onCreated: selectView,
        });

    const validateCaseName =
        (exceptCaseId: string | undefined) =>
        (trimmed: string): string | undefined => {
            if (trimmed === '') {
                return 'patientList.cases.nameEmpty';
            }
            if (trimmed.length > CASE_NAME_MAX_LENGTH) {
                return 'patientList.cases.nameTooLong';
            }
            const taken = (data?.cases ?? []).some(
                (c) =>
                    c.id !== exceptCaseId &&
                    c.name?.toLocaleLowerCase() === trimmed.toLocaleLowerCase()
            );
            return taken ? 'patientList.cases.nameTaken' : undefined;
        };

    const handleNamed = (name: string) => {
        const current = naming;
        setNaming(undefined);
        if (current?.mode === 'rename') {
            runCaseCommand({
                type: 'rename',
                caseId: current.caseId,
                name,
            });
        } else if (current === undefined) {
            return;
        } else {
            const leftOut = primaryLeftOutOfFirstCase(data, current.datasetIds);
            if (leftOut !== undefined) {
                setPendingFirstCase({
                    name,
                    datasetIds: current.datasetIds,
                    leftOut,
                });
            } else {
                createCase(name, current.datasetIds);
            }
        }
    };

    const handleCaseAction = (action: CaseAction, caseId: string) => {
        switch (action) {
            case 'rename':
                setNaming({
                    mode: 'rename',
                    caseId,
                    name: data?.cases?.find((c) => c.id === caseId)?.name ?? '',
                });
                return;
            case 'workCase':
                runCaseCommand({ type: 'workCase', caseId });
                return;
            case 'archive':
            case 'restore':
                runCaseCommand({
                    type: 'archive',
                    caseId,
                    archived: action === 'archive',
                });
                return;
            case 'delete':
                setPendingDelete(caseId);
                return;
        }
    };

    /**
     * The clicked dataset's display name, for the switch confirmation.
     *
     * The confirmation names only this one. Two series can share a
     * description — two reconstructions, two phases — so a message naming
     * both the outgoing and the incoming image would read as one image
     * replacing itself. The one being replaced is whichever row wears the
     * filled star, in view behind the dialog — which is why the message
     * points at the star rather than at a name or a position.
     */
    const nameOf = (datasetId: string | undefined) => {
        const series = allSeries.find((s) => s.id === datasetId);
        return (
            <SensitiveInfoTypography key="name">
                {series
                    ? seriesDisplayName(intl, series)
                    : intl.formatMessage({ id: 'series' })}
            </SensitiveInfoTypography>
        );
    };

    const openInViewer = useOpenInViewer();
    const openBusy = useIsOpeningInViewer();
    // Open follows what is in view: the datasets ticked, else the case in
    // view, else the whole patient.
    const openScope: CaseScope | undefined =
        ticked.size > 0
            ? { datasetIds: [...ticked], fromCaseId: viewCaseId }
            : viewCaseId !== undefined
              ? { caseId: viewCaseId }
              : undefined;
    const handleOpen = () => {
        if (openScope === undefined) {
            openInViewer(patientId, 'all');
        } else {
            openInViewer(patientId, 'all', { scope: openScope });
        }
    };
    // The same refusal the open itself applies, said on the control before
    // the click.
    const scoped =
        data === undefined ? undefined : scopeToOpen(data, openScope);
    const openRefusal =
        scoped !== undefined && 'refusal' in scoped
            ? scoped.refusal
            : undefined;
    // Open needs the loaded detail to resolve datasets from.
    const canOpen =
        data !== undefined && !openBusy && openRefusal === undefined;
    const openLabel =
        ticked.size > 0 ? (
            intl.formatMessage(
                { id: 'patientList.cases.openTicked' },
                { count: ticked.size }
            )
        ) : viewCaseId !== undefined ? (
            // One span: the button is a flex box and would drop the space before the name.
            <span>
                {intl.formatMessage(
                    { id: 'patientList.cases.openCase' },
                    {
                        name: (
                            <SensitiveInfoTypography key="name">
                                {viewCase?.name ?? ''}
                            </SensitiveInfoTypography>
                        ),
                    }
                )}
            </span>
        ) : (
            intl.formatMessage({ id: 'patientList.drilldown.open' })
        );

    return (
        <Box
            ref={rootRef}
            tabIndex={-1}
            role="region"
            aria-label={intl.formatMessage({
                id: 'patientList.drilldown.region',
            })}
            sx={{
                width,
                flexShrink: 0,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
                overflow: 'hidden',
            }}
        >
            <DrilldownHeader
                patient={data}
                onClose={onClose}
                actions={
                    <>
                        <DisabledHint
                            title={intl.formatMessage({
                                id: 'patientList.drilldown.exportHint',
                            })}
                        >
                            <Button
                                variant="outlined"
                                startIcon={<Download size={14} />}
                                disabled
                            >
                                <FormattedMessage id="patientList.drilldown.export" />
                            </Button>
                        </DisabledHint>
                        {canOpen && !casesSaving ? (
                            <Tooltip
                                // Describes the button; its name stays the visible label.
                                describeChild
                                title={intl.formatMessage({
                                    id: 'patientList.drilldown.openHint',
                                })}
                            >
                                <Button
                                    variant="contained"
                                    onClick={handleOpen}
                                >
                                    {openLabel}
                                </Button>
                            </Tooltip>
                        ) : (
                            <DisabledHint
                                title={intl.formatMessage({
                                    id: openBusy
                                        ? 'patientList.open.inFlight'
                                        : casesSaving
                                          ? 'patientList.cases.openWhileSaving'
                                          : openRefusal === 'caseHasNoImage'
                                            ? 'patientList.cases.caseHasNoImage'
                                            : openRefusal ===
                                                'selectionHasNoImage'
                                              ? 'patientList.cases.openNoImage'
                                              : 'patientList.drilldown.openUnavailableHint',
                                })}
                            >
                                <Button variant="contained" disabled>
                                    {openLabel}
                                </Button>
                            </DisabledHint>
                        )}
                    </>
                }
                onSetCourseStage={canModify ? setCourseStage : undefined}
                stageSaving={isSaving}
            />
            {stageError && (
                <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                    <FormattedMessage id="patientList.stage.saveFailed" />
                </Alert>
            )}
            {primaryError && (
                <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                    <FormattedMessage id="patientList.primary.saveFailed" />
                </Alert>
            )}
            {casesError && (
                <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                    <FormattedMessage id="patientList.cases.saveFailed" />
                </Alert>
            )}
            {data !== undefined && (
                <CaseChips
                    cases={activeCases}
                    archivedCases={archivedCases}
                    workCaseId={data.workCaseId ?? undefined}
                    selectedCaseId={viewCaseId}
                    allCount={allSeries.length}
                    onSelect={selectView}
                    onAction={canModify ? handleCaseAction : undefined}
                    newCaseCount={newCaseIds.length}
                    onNewCase={
                        canModify
                            ? () =>
                                  setNaming({
                                      mode: 'create',
                                      datasetIds: newCaseIds,
                                      name: defaultCaseName(data.cases ?? []),
                                  })
                            : undefined
                    }
                    busy={casesSaving}
                />
            )}
            {ticked.size > 0 && (
                <CaseSelectionBar
                    count={ticked.size}
                    cases={activeCases}
                    selectedCaseId={viewCaseId}
                    onAddTo={
                        canModify
                            ? (caseId) =>
                                  runCaseCommand(
                                      {
                                          type: 'members',
                                          caseId,
                                          add: [...ticked],
                                      },
                                      undefined,
                                      clearTicks
                                  )
                            : undefined
                    }
                    removeRefusal={
                        viewCaseId !== undefined &&
                        data !== undefined &&
                        !caseKeepsAnImage(data, viewCaseId, [...ticked])
                            ? 'patientList.cases.removeLeavesNoImage'
                            : undefined
                    }
                    onRemove={
                        canModify
                            ? () => {
                                  if (
                                      viewCaseId === undefined ||
                                      data === undefined
                                  ) {
                                      return;
                                  }
                                  const datasetIds = [...ticked];
                                  const alsoLeaving = setsLeavingWithImages(
                                      data,
                                      viewCaseId,
                                      datasetIds
                                  );
                                  if (alsoLeaving.length > 0) {
                                      setPendingRemoval({
                                          caseId: viewCaseId,
                                          datasetIds,
                                          alsoLeaving,
                                      });
                                  } else {
                                      removeFromCase(viewCaseId, datasetIds);
                                  }
                              }
                            : undefined
                    }
                    onClear={clearTicks}
                    busy={casesSaving}
                />
            )}
            {data !== undefined && viewSeries.length > 0 && (
                <DatasetToolbar
                    composition={composition}
                    modalityFilter={shownFilter}
                    onToggleModality={toggleModality}
                    shownCount={shownSeries.length}
                    totalCount={viewSeries.length}
                    studyCount={viewDetail?.studies?.length ?? 0}
                    tickState={tickState}
                    onTickAll={handleTickAll}
                />
            )}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <PatientImportSection retrievals={retrievals} />
                {isLoading && (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={24} />
                    </Box>
                )}
                {isError && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        <FormattedMessage id="patientList.drilldown.error" />
                    </Alert>
                )}
                {/* A patient whose requested series have not arrived yet is not an
                    empty record: the import section above says what is coming. Until
                    the retrievals have been read, nothing says the record is empty. */}
                {!isLoading &&
                    !isError &&
                    !retrievals.isLoading &&
                    !retrievals.isError &&
                    allSeries.length === 0 &&
                    retrievals.summary.total === 0 && (
                        <Alert severity="info" sx={{ m: 2 }}>
                            <FormattedMessage id="patientList.drilldown.emptyAll" />
                        </Alert>
                    )}
                {groups.map((group) => (
                    <DatasetDayGroup
                        key={group.day}
                        group={group}
                        mode="patient"
                        primary={primary}
                        ticked={ticked}
                        onTick={handleTick}
                        info={infoOffer}
                        infoOpenKey={info.openKey}
                        infoPinnedKey={info.pinnedKey}
                    />
                ))}
            </Box>

            <ConfirmationDialog
                open={pendingImageSwitch !== undefined}
                mode="primary"
                title={intl.formatMessage({
                    id: 'patientList.primary.image.switchTitle',
                })}
                message={
                    <>
                        {intl.formatMessage(
                            { id: 'patientList.primary.image.switchMessage' },
                            { name: nameOf(pendingImageSwitch) }
                        )}
                        {/* Stated only when there is one to lose. The set is
                            contoured on the image being replaced, so it cannot
                            survive the switch — and losing it silently would be
                            a second change the user did not ask for. */}
                        {primaries.structureSetId
                            ? intl.formatMessage({
                                  id: 'patientList.primary.image.switchDropsSet',
                              })
                            : null}
                    </>
                }
                confirmText={intl.formatMessage({
                    id: 'patientList.primary.image.switchConfirm',
                })}
                onCancel={() => setPendingImageSwitch(undefined)}
                onConfirm={() => {
                    if (pendingImageSwitch !== undefined) {
                        setPrimaryDataset({
                            kind: 'image',
                            datasetId: pendingImageSwitch,
                            caseId: viewCaseId,
                        });
                    }
                    setPendingImageSwitch(undefined);
                }}
            />

            <NameDialog
                open={naming !== undefined}
                title={intl.formatMessage({
                    id:
                        naming?.mode === 'rename'
                            ? 'patientList.cases.renameTitle'
                            : 'patientList.cases.newTitle',
                })}
                label={intl.formatMessage({
                    id: 'patientList.cases.nameLabel',
                })}
                confirmText={intl.formatMessage({
                    id:
                        naming?.mode === 'rename'
                            ? 'rename'
                            : 'patientList.cases.create',
                })}
                initialValue={naming?.name ?? ''}
                confirmUnchanged={naming?.mode === 'create'}
                validate={validateCaseName(
                    naming?.mode === 'rename' ? naming.caseId : undefined
                )}
                onCancel={() => setNaming(undefined)}
                onConfirm={handleNamed}
            />

            <ConfirmationDialog
                open={pendingFirstCase !== undefined}
                mode="primary"
                title={intl.formatMessage({
                    id:
                        pendingFirstCase?.leftOut.kind === 'structureSet'
                            ? 'patientList.cases.firstCaseTitleStructureSet'
                            : 'patientList.cases.firstCaseTitle',
                })}
                message={intl.formatMessage(
                    {
                        id:
                            pendingFirstCase?.leftOut.kind === 'structureSet'
                                ? 'patientList.cases.firstCaseDropsStructureSet'
                                : 'patientList.cases.firstCaseDropsPrimary',
                    },
                    { name: nameOf(pendingFirstCase?.leftOut.datasetId) }
                )}
                confirmText={intl.formatMessage({
                    id: 'patientList.cases.create',
                })}
                onCancel={() => setPendingFirstCase(undefined)}
                onConfirm={() => {
                    if (pendingFirstCase !== undefined) {
                        createCase(
                            pendingFirstCase.name,
                            pendingFirstCase.datasetIds
                        );
                    }
                    setPendingFirstCase(undefined);
                }}
            />

            <ConfirmationDialog
                open={pendingRemoval !== undefined}
                mode="warning"
                title={intl.formatMessage({
                    id: 'patientList.cases.removeTitle',
                })}
                message={intl.formatMessage(
                    { id: 'patientList.cases.removeAlsoSets' },
                    {
                        count: pendingRemoval?.alsoLeaving.length ?? 0,
                        names: (
                            <SensitiveInfoTypography key="names">
                                {(pendingRemoval?.alsoLeaving ?? [])
                                    .map((set) => seriesDisplayName(intl, set))
                                    .join(', ')}
                            </SensitiveInfoTypography>
                        ),
                    }
                )}
                confirmText={intl.formatMessage({
                    id: 'patientList.cases.remove',
                })}
                onCancel={() => setPendingRemoval(undefined)}
                onConfirm={() => {
                    if (pendingRemoval !== undefined) {
                        removeFromCase(
                            pendingRemoval.caseId,
                            pendingRemoval.datasetIds
                        );
                    }
                    setPendingRemoval(undefined);
                }}
            />

            <ConfirmationDialog
                open={pendingDelete !== undefined}
                mode="error"
                title={intl.formatMessage({
                    id: 'patientList.cases.deleteTitle',
                })}
                message={intl.formatMessage(
                    { id: 'patientList.cases.deleteMessage' },
                    {
                        name: (
                            <SensitiveInfoTypography key="name">
                                {data?.cases?.find(
                                    (c) => c.id === pendingDelete
                                )?.name ?? ''}
                            </SensitiveInfoTypography>
                        ),
                    }
                )}
                confirmText={intl.formatMessage({
                    id: 'patientList.cases.delete',
                })}
                onCancel={() => setPendingDelete(undefined)}
                onConfirm={() => {
                    if (pendingDelete !== undefined) {
                        // The view leaves the case only once it is gone.
                        runCaseCommand(
                            { type: 'delete', caseId: pendingDelete },
                            undefined,
                            () => selectView(undefined)
                        );
                    }
                    setPendingDelete(undefined);
                }}
            />
        </Box>
    );
}
